import Vue from 'vue';
import Router from 'vue-router';
import Home from './components/Home.vue';
import Login from './components/Login.vue';
import Overview from './components/Overview.vue';
import AdminPanel from './views/AdminPanel.vue';
import DcmManagement from './components/DcmManagement.vue';
import SponsorManagement from './components/SponsorManagement.vue';
import DcmDashboard from './components/DcmDashboard.vue';
import CreativeDashboard from './components/CreativeDashboard.vue';
import CampaignStatusDashboard from './components/CampaignStatusDashboard.vue';
import UploadStudio from './components/UploadStudio.vue';
import SponsorReportSection from './components/SponsorReportSection.vue';
import UploadLinksHistory from './components/UploadLinksHistory.vue';
import api from './api';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    { path: '/login', component: Login },
    { path: '/overview', component: Overview, meta: { requiresAuth: true } },
    { path: '/admin', component: AdminPanel, meta: { requiresAuth: true, requiresSuper: true } },
    { path: '/admin/users', component: AdminPanel, meta: { requiresAuth: true, requiresSuper: true } },
    { path: '/admin/features', component: AdminPanel, meta: { requiresAuth: true, requiresSuper: true } },
    { path: '/dcm-management', component: DcmManagement, meta: { requiresAuth: true, allowedRoles: ['admin', 'marketing', 'markting', 'dcm'] } },
    { path: '/dcm-dashboard', component: DcmDashboard, meta: { requiresAuth: true, allowedRoles: ['dcm'] } },
    { path: '/creative-dashboard', component: CreativeDashboard, meta: { requiresAuth: true, allowedRoles: ['creative'] } },
    { path: '/campaign-dashboard', component: CampaignStatusDashboard, meta: { requiresAuth: true } },
    { path: '/sponsor-reports', component: SponsorReportSection, meta: { requiresAuth: true, allowedRoles: ['admin', 'marketing', 'markting'] } },
    { path: '/upload-studio', component: UploadStudio, meta: { requiresAuth: true, allowedRoles: ['dcm'] } },
    { path: '/upload-links', component: UploadLinksHistory, meta: { requiresAuth: true, allowedRoles: ['admin', 'dcm'] } },
    { path: '/sponsor-management', component: SponsorManagement, meta: { requiresAuth: true, allowedRoles: ['admin', 'marketing', 'markting'] } },
    { path: '/public/sponsor-report/:token', component: () => import('./components/PublicSponsorReport.vue') },
    { path: '/', component: Home, meta: { requiresAuth: true } },
    { path: '/smm-report', component: () => import('./components/SMMReport.vue'), meta: { requiresAuth: true } }
  ]
});

router.beforeEach(async (to, from, next) => {
  try {
    const res = await api.get('/check');
    const isLoggedIn = res.data.logged_in;
    const isSuperUser = !!res.data.isSuperUser;
    const userRole = String(res.data.role || '').toLowerCase();
    const enabledFeatures = Array.isArray(res.data.enabledFeatures) ? res.data.enabledFeatures : [];

    const isDcmUser = !isSuperUser && userRole === 'dcm';
    const isCreativeUser = !isSuperUser && userRole === 'creative';

    // can access user management if super, admin role, or has users_management feature
    const canManageUsers = isSuperUser || userRole === 'admin' || enabledFeatures.includes('users_management');

    // ✅ Prevent logged-in users from accessing the login page
    if (to.path === '/login' && isLoggedIn) {
      if (isDcmUser) return next('/dcm-dashboard');
      if (isCreativeUser) return next('/creative-dashboard');
      return next('/');
    }

    // ✅ Redirect to login if route requires auth and not logged in
    if (to.matched.some(record => record.meta.requiresAuth) && !isLoggedIn) {
      return next('/login');
    }

    if (to.matched.some(record => record.meta.requiresSuper) && !canManageUsers) {
      return next('/overview');
    }

    const requiresRolesRoute = to.matched.find(record => Array.isArray(record.meta.allowedRoles));
    if (requiresRolesRoute) {
        const allowedRoles = requiresRolesRoute.meta.allowedRoles.map(role => String(role).toLowerCase());
        if (!isSuperUser && !allowedRoles.includes(userRole)) {
          if (isDcmUser) return next('/dcm-dashboard');
          if (isCreativeUser) return next('/creative-dashboard');
          return next('/overview');
        }
    }

    if (to.path === '/' && isDcmUser) {
      return next('/dcm-dashboard');
    }

    if (to.path === '/' && isCreativeUser) {
      return next('/creative-dashboard');
    }

    // ✅ All good
    next();
  } catch (err) {
    // 🔴 If error happens (like network), force login if route needs auth
    if (to.matched.some(record => record.meta.requiresAuth)) {
      return next('/login');
    }

    next();
  }
});


export default router;
